import {
  Icon,
  IconButton,
  ProgressIndicator,
  Stack,
  Text,
} from "office-ui-fabric-react";
import {
  DefaultButton,
  PrimaryButton,
} from "office-ui-fabric-react/lib/Button";
import {
  Dialog,
  DialogFooter,
  DialogType,
} from "office-ui-fabric-react/lib/Dialog";
import * as React from "react";
import { downloadQueueStore, DownloadQueueItem } from "./downloadQueueStore";

interface IDownloadManagerState {
  items: DownloadQueueItem[];
  collapsed: boolean;
  dismissed: boolean;
  showCancelConfirm: boolean;
}

const cancelConfirmDialogContentProps = {
  type: DialogType.normal,
  title: "Cancel downloads?",
  subText:
    "Downloads still in progress will be stopped and cannot be resumed.",
};

function formatEta(seconds?: number): string {
  if (seconds === undefined || !isFinite(seconds) || seconds <= 0) {
    return "";
  }
  if (seconds < 60) {
    return `${Math.ceil(seconds)}s left`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.ceil(seconds % 60);
  return `${minutes}m ${remainingSeconds}s left`;
}

// Non-blocking, floating download-progress widget (mount once near the app root).
// Downloads run against downloadQueueStore regardless of whether this is mounted -
// it only reflects state, it does not own the download lifecycle. Mirrors UploadManager.tsx.
export default class DownloadManager extends React.Component<
  {},
  IDownloadManagerState
> {
  private unsubscribe?: () => void;

  constructor(props: {}) {
    super(props);
    this.state = {
      items: downloadQueueStore.getItems(),
      collapsed: false,
      dismissed: false,
      showCancelConfirm: false,
    };
  }

  componentDidMount() {
    this.unsubscribe = downloadQueueStore.subscribe(() => {
      this.setState({
        items: downloadQueueStore.getItems(),
        dismissed: false,
      });
    });
    window.addEventListener("beforeunload", this.warnOnActiveDownloads);
  }

  componentWillUnmount() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    window.removeEventListener("beforeunload", this.warnOnActiveDownloads);
  }

  private warnOnActiveDownloads = (e: BeforeUnloadEvent) => {
    const hasActiveDownloads = downloadQueueStore
      .getItems()
      .some((it) => it.status === "downloading");
    if (hasActiveDownloads) {
      e.preventDefault();
      e.returnValue = "";
    }
  };

  private toggleCollapse = () => {
    this.setState((s) => ({ collapsed: !s.collapsed }));
  };

  private close = () => {
    const hasActiveDownloads = downloadQueueStore
      .getItems()
      .some((it) => it.status === "downloading");
    if (hasActiveDownloads) {
      this.setState({ showCancelConfirm: true });
      return;
    }
    downloadQueueStore.clearCompleted();
    this.setState({ dismissed: true });
  };

  private confirmCancelAll = () => {
    downloadQueueStore.cancelAll();
    this.setState({ showCancelConfirm: false });
  };

  private dismissCancelConfirm = () => {
    this.setState({ showCancelConfirm: false });
  };

  render() {
    const { items, collapsed, dismissed, showCancelConfirm } = this.state;
    if (items.length === 0 || dismissed) {
      return null;
    }

    const downloadingCount = items.filter(
      (it) => it.status === "downloading",
    ).length;
    const title =
      downloadingCount > 0
        ? `Downloading ${downloadingCount} item${downloadingCount > 1 ? "s" : ""}...`
        : `${items.length} item${items.length > 1 ? "s" : ""} downloaded`;

    return (
      <div
        style={{
          position: "fixed",
          bottom: 16,
          right: 16,
          width: 500,
          background: "#fff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
          borderRadius: 4,
          // above Fluent's Panel/Overlay Layer (z-index 1000000) so the widget
          // stays visible while the comments panel is open
          zIndex: 1000001,
        }}
      >
        <Stack
          horizontal
          horizontalAlign="space-between"
          verticalAlign="center"
          styles={{
            root: {
              padding: "8px 12px",
              borderBottom: collapsed ? "none" : "1px solid #eee",
            },
          }}
        >
          <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
            {title}
          </Text>
          <Stack horizontal>
            <IconButton
              iconProps={{ iconName: collapsed ? "ChevronUp" : "ChevronDown" }}
              onClick={this.toggleCollapse}
              ariaLabel="Toggle download list"
            />
            <IconButton
              iconProps={{ iconName: "Cancel" }}
              onClick={this.close}
              ariaLabel="Dismiss"
            />
          </Stack>
        </Stack>
        {!collapsed && (
          <Stack
            styles={{
              root: {
                maxHeight: 280,
                overflowY: "auto",
                padding: "4px 12px 8px",
              },
            }}
          >
            {items.map((it) => (
              <Stack key={it.id} styles={{ root: { padding: "6px 0" } }}>
                <Stack
                  horizontal
                  horizontalAlign="space-between"
                  verticalAlign="center"
                >
                  <Text
                    variant="medium"
                    styles={{
                      root: {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: 300,
                      },
                    }}
                  >
                    {it.fileName}
                  </Text>
                  {it.status === "done" ? (
                    <Icon
                      iconName="CheckMark"
                      styles={{ root: { color: "#107c10" } }}
                    />
                  ) : it.status === "error" ? (
                    <Icon
                      iconName="ErrorBadge"
                      styles={{ root: { color: "#a4262c" } }}
                    />
                  ) : it.status === "cancelled" ? (
                    <Icon
                      iconName="Cancel"
                      styles={{ root: { color: "#605e5c" } }}
                    />
                  ) : (
                    <Stack horizontal verticalAlign="center">
                      <Text variant="small">
                        {Math.round(it.progress * 100)}%
                      </Text>
                      <IconButton
                        iconProps={{ iconName: "Cancel" }}
                        onClick={() => downloadQueueStore.cancel(it.id)}
                        ariaLabel={`Cancel download of ${it.fileName}`}
                        styles={{
                          root: { width: 20, height: 20 },
                          icon: { fontSize: 10 },
                        }}
                      />
                    </Stack>
                  )}
                </Stack>
                {it.status === "error" && (
                  <Text variant="small" styles={{ root: { color: "#a4262c" } }}>
                    {it.errorMessage}
                  </Text>
                )}
                {it.status === "downloading" && (
                  <>
                    <ProgressIndicator
                      percentComplete={it.progress}
                      barHeight={4}
                    />
                    {formatEta(it.etaSeconds) && (
                      <Text
                        variant="tiny"
                        styles={{ root: { color: "#605e5c" } }}
                      >
                        {formatEta(it.etaSeconds)}
                      </Text>
                    )}
                  </>
                )}
                {it.status === "cancelled" && (
                  <Text variant="small" styles={{ root: { color: "#605e5c" } }}>
                    {it.errorMessage}
                  </Text>
                )}
              </Stack>
            ))}
          </Stack>
        )}
        <Dialog
          isOpen={showCancelConfirm}
          onDismiss={this.dismissCancelConfirm}
          dialogContentProps={cancelConfirmDialogContentProps}
          closeButtonAriaLabel="Close"
        >
          <DialogFooter>
            <PrimaryButton
              onClick={this.confirmCancelAll}
              text="Cancel all downloads"
            />
            <DefaultButton
              onClick={this.dismissCancelConfirm}
              text="Continue downloading"
            />
          </DialogFooter>
        </Dialog>
      </div>
    );
  }
}
